import { LaunchOutlined } from '@mui/icons-material'
import { Box, Button, Chip, Stack, Typography } from '@mui/material'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  PortfolioCategory,
  PortfolioProject
} from '@/pages/Portfolio/data'

type Props = {
  images: PortfolioProject[]
  title: string
  embedded?: boolean
}

const FILTERS: PortfolioCategory[] = ['all', 'web', 'tools', 'ecommerce']

const PortfolioGallery: React.FC<Props> = ({ images, title, embedded }) => {
  const { t } = useTranslation()
  const [filter, setFilter] = useState<PortfolioCategory>('all')

  const filtered = useMemo(() => {
    if (filter === 'all') return images
    return images.filter((item) => item.category === filter)
  }, [filter, images])

  return (
    <Box sx={{ width: '100%', pb: { xs: 2, md: 4 } }}>
      <Box sx={{ textAlign: 'center', py: { xs: 2, md: 3 }, px: 1 }}>
        <Typography
          variant="h4"
          component={embedded ? 'h2' : 'h1'}
          fontWeight="bold">
          {title}
        </Typography>
        <Typography
          mt={1.5}
          color="text.secondary"
          sx={{
            maxWidth: 520,
            mx: 'auto',
            fontSize: { xs: '0.875rem', sm: '1rem' }
          }}>
          {t('my_portfolio.subtitle')}
        </Typography>
      </Box>

      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        flexWrap="wrap"
        justifyContent="center"
        sx={{ mb: 3, px: 0.5 }}>
        {FILTERS.map((key) => (
          <Chip
            key={key}
            clickable
            color={filter === key ? 'secondary' : 'default'}
            variant={filter === key ? 'filled' : 'outlined'}
            label={t(`my_portfolio.filter_${key}`)}
            onClick={() => setFilter(key)}
            size="medium"
            sx={{
              transition: 'transform 0.2s ease',
              '&:hover': { transform: 'translateY(-2px)' },
              fontSize: { xs: '0.75rem', sm: '0.8125rem' }
            }}
          />
        ))}
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            md: 'repeat(3, minmax(0, 1fr))'
          },
          gap: { xs: 2, sm: 2.5 },
          alignItems: 'stretch'
        }}>
        {filtered.map((item) => {
          const projectTitle = t(`my_portfolio.projects.${item.id}.title`)
          const description = t(
            `my_portfolio.projects.${item.id}.description`
          )

          return (
            <Box
              key={item.id}
              sx={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                minHeight: { xs: 380, sm: 400, md: 420 },
                height: '100%',
                borderRadius: 3,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'background.paper',
                boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
                transition: 'box-shadow 0.25s ease',
                '&:hover': {
                  boxShadow: '0 16px 36px rgba(0,0,0,0.12)'
                },
                '&:hover .portfolio-media': {
                  transform: 'scale(1.06)'
                },
                '&:hover .portfolio-overlay': {
                  opacity: 1,
                  visibility: 'visible'
                }
              }}>
              <Box
                sx={{
                  position: 'relative',
                  height: { xs: 180, sm: 200 },
                  flexShrink: 0,
                  overflow: 'hidden',
                  background: `linear-gradient(145deg, ${item.accent}33, ${item.accent})`
                }}>
                {item.src ? (
                  <Box
                    className="portfolio-media"
                    component="img"
                    src={item.src}
                    alt={projectTitle}
                    width={440}
                    height={200}
                    loading="lazy"
                    decoding="async"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                      transition: 'transform 0.45s ease'
                    }}
                  />
                ) : (
                  <Box
                    className="portfolio-media"
                    sx={{
                      width: '100%',
                      height: '100%',
                      display: 'grid',
                      placeItems: 'center',
                      transition: 'transform 0.45s ease'
                    }}>
                    <Typography
                      variant="h3"
                      component="span"
                      fontWeight={800}
                      sx={{ color: '#fff', letterSpacing: 1 }}>
                      {projectTitle.slice(0, 2).toUpperCase()}
                    </Typography>
                  </Box>
                )}

                <Box
                  className="portfolio-overlay"
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'grid',
                    placeItems: 'center',
                    background:
                      'linear-gradient(180deg, rgba(0,0,0,0.2), rgba(0,0,0,0.6))',
                    opacity: { xs: 1, sm: 0 },
                    visibility: { xs: 'visible', sm: 'hidden' },
                    transition: 'opacity 0.25s ease'
                  }}>
                  <Button
                    component="a"
                    href={item.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="contained"
                    color="secondary"
                    size="medium"
                    endIcon={<LaunchOutlined />}
                    sx={{ borderRadius: 5, px: 2.5 }}>
                    {t('my_portfolio.demo')}
                  </Button>
                </Box>
              </Box>

              <Box
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  flex: 1,
                  minHeight: 0
                }}>
                <Typography
                  variant="h6"
                  component="h3"
                  fontWeight={700}
                  lineHeight={1.3}
                  sx={{
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    minHeight: { xs: 44, sm: 52 }
                  }}>
                  {projectTitle}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                  {description}
                </Typography>
                <Stack
                  direction="row"
                  spacing={0.75}
                  useFlexGap
                  flexWrap="wrap"
                  sx={{ mt: 'auto', pt: 1 }}>
                  {item.tech.map((tech) => (
                    <Chip
                      key={tech}
                      label={tech}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Stack>
              </Box>
            </Box>
          )
        })}
      </Box>

      {filtered.length === 0 && (
        <Typography textAlign="center" color="text.secondary" py={4}>
          {t('my_portfolio.empty')}
        </Typography>
      )}
    </Box>
  )
}

export default PortfolioGallery
